// src/components/tiptap/extensions/YouTubeLinkNode.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer, NodeViewWrapper } from "@tiptap/react";
import { supabase } from "../../../config/supabase";
import * as FiIcons from "react-icons/fi";
import SafeIcon from "../../../common/SafeIcon.jsx";

const { FiVideo, FiSave, FiTrash2, FiExternalLink } = FiIcons;

const cls = (...xs) => xs.filter(Boolean).join(" ");

/** helper to allow form controls to keep default behavior */
const isFormControl = (el) =>
  el &&
  (el.tagName === "SELECT" ||
    el.tagName === "INPUT" ||
    el.tagName === "TEXTAREA" ||
    el.isContentEditable);

/**
 * Extract YouTube video_id + start seconds from common URL formats:
 * - https://www.youtube.com/watch?v=ID&t=43s
 * - https://youtu.be/ID?t=43
 * - https://www.youtube.com/shorts/ID
 * - https://www.youtube.com/embed/ID?start=43
 */
function parseYouTubeUrl(raw) {
  const input = String(raw || "").trim();
  if (!input) return { ok: false, videoId: null, startSeconds: 0 };

  // If someone pastes just the ID, allow it.
  const idLike = input.match(/^[a-zA-Z0-9_-]{6,}$/);
  if (idLike) return { ok: true, videoId: input, startSeconds: 0 };

  let url;
  try {
    url = new URL(input);
  } catch {
    // Try to salvage missing scheme
    try {
      url = new URL(`https://${input}`);
    } catch {
      return { ok: false, videoId: null, startSeconds: 0 };
    }
  }

  const host = url.hostname.replace(/^www\./, "");
  const path = url.pathname || "/";

  let videoId = null;

  // youtu.be/<id>
  if (host === "youtu.be") {
    const parts = path.split("/").filter(Boolean);
    videoId = parts[0] || null;
  }

  // youtube.com/watch?v=<id>
  if (!videoId && (host === "youtube.com" || host === "m.youtube.com")) {
    const v = url.searchParams.get("v");
    if (v) videoId = v;
  }

  // youtube.com/shorts/<id>
  if (!videoId && (host === "youtube.com" || host === "m.youtube.com") && path.startsWith("/shorts/")) {
    videoId = path.split("/shorts/")[1]?.split("/")[0] || null;
  }

  // youtube.com/embed/<id>
  if (!videoId && (host === "youtube.com" || host === "m.youtube.com") && path.startsWith("/embed/")) {
    videoId = path.split("/embed/")[1]?.split("/")[0] || null;
  }

  // Pull start seconds from:
  // - t=43 or t=43s or t=1m2s
  // - start=43
  // - ?time_continue=43 (rare)
  const t = url.searchParams.get("t") || url.searchParams.get("start") || url.searchParams.get("time_continue");
  const startSeconds = Math.max(0, parseTimeToSeconds(t));

  const ok = !!videoId;
  return { ok, videoId, startSeconds };
}

function parseTimeToSeconds(t) {
  if (!t) return 0;
  const s = String(t).trim();

  // 43 or 43s
  const simple = s.match(/^(\d+)\s*s?$/i);
  if (simple) return Number(simple[1]) || 0;

  // 1m2s, 2m, 1h2m3s, etc
  let total = 0;
  const re = /(\d+)\s*(h|m|s)/gi;
  let m;
  while ((m = re.exec(s))) {
    const n = Number(m[1]) || 0;
    const unit = m[2].toLowerCase();
    if (unit === "h") total += n * 3600;
    if (unit === "m") total += n * 60;
    if (unit === "s") total += n;
  }
  return total;
}

function buildEmbedUrl(videoId, startSeconds, origin) {
  if (!videoId) return null;

  // Compatibility-first: youtube-nocookie + no-referrer can trigger Error 153 in this app.
  const u = new URL(`https://www.youtube.com/embed/${videoId}`);
  u.searchParams.set("rel", "0");
  u.searchParams.set("modestbranding", "1");
  u.searchParams.set("playsinline", "1");
  if (origin) u.searchParams.set("origin", origin);
  if (startSeconds && Number(startSeconds) > 0) u.searchParams.set("start", String(Number(startSeconds)));
  return u.toString();
}

function YouTubeLinkBlock({ node, updateAttributes, editor, deleteNode }) {
  const attrs = node.attrs || {};
  const ctx = editor?.storage?.mms ?? {};

  // YouTube sometimes initializes at the wrong size in TipTap/nodeviews.
  // We observe the container size and force-remount the iframe when it changes.
  const playerRef = useRef(null);
  const [iframeKey, setIframeKey] = useState(0);

  const canEdit = !!ctx?.userId && !!editor?.isEditable; // teachers only
  const saved = !!attrs.recordId;

  const [url, setUrl] = useState(attrs.youtubeUrl ?? "");
  const parsed = useMemo(() => parseYouTubeUrl(url), [url]);

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Ensure noteBlockId exists once
  useEffect(() => {
    if (!attrs.noteBlockId) {
      updateAttributes({
        noteBlockId: crypto.randomUUID?.() ?? String(Date.now()),
      });
    }
  }, [attrs.noteBlockId, updateAttributes]);

  // If attrs change (e.g. rehydrate), keep input in sync if user hasn't typed
  useEffect(() => {
    if ((attrs.youtubeUrl ?? "") && (url ?? "") === "") {
      setUrl(attrs.youtubeUrl ?? "");
    }
  }, [attrs.youtubeUrl, url]);

  const effectiveLessonNoteId = attrs.lessonNoteId ?? ctx.lessonNoteId ?? null;

  const canSave =
    canEdit &&
    !!effectiveLessonNoteId &&
    parsed.ok &&
    !!url.trim();

  const handleSave = async () => {
    if (!canEdit) return;
    if (!effectiveLessonNoteId) return alert("Missing context: need lessonNoteId to save this tool.");
    if (!parsed.ok) return alert("Please paste a valid YouTube link.");

    const nbid = attrs.noteBlockId ?? (crypto.randomUUID?.() ?? String(Date.now()));

    setSaving(true);
    const { data, error } = await supabase.rpc("get_or_create_lesson_note_youtube_link", {
      p_note_block_id: nbid,
      p_school_id: attrs.schoolId ?? ctx.schoolId ?? null,
      p_lesson_note_id: effectiveLessonNoteId,
      p_created_by: ctx.userId ?? null,
      p_teacher_id: attrs.teacherId ?? ctx.userId ?? null,
      p_student_id: attrs.studentId ?? ctx.studentId ?? null,
      p_student_master_id: attrs.studentMasterId ?? ctx.masterStudentId ?? null,
      p_youtube_url: url.trim(),
      p_youtube_video_id: parsed.videoId,
      p_start_seconds: parsed.startSeconds ?? 0,
    });
    setSaving(false);

    if (error) return alert(error.message);

    updateAttributes({
      recordId: data?.id ?? attrs.recordId ?? null,
      lessonNoteId: effectiveLessonNoteId,
      schoolId: attrs.schoolId ?? ctx.schoolId ?? null,
      teacherId: attrs.teacherId ?? ctx.userId ?? null,
      studentId: attrs.studentId ?? ctx.studentId ?? null,
      studentMasterId: attrs.studentMasterId ?? ctx.masterStudentId ?? null,
      noteBlockId: nbid,
      youtubeUrl: url.trim(),
      youtubeVideoId: parsed.videoId,
      startSeconds: parsed.startSeconds ?? 0,
    });
  };

  const handleDelete = async () => {
    if (!canEdit) return;
    if (!attrs.recordId) {
      deleteNode?.();
      return;
    }

    setDeleting(true);
    const { error } = await supabase.rpc("delete_lesson_note_youtube_link", { p_id: attrs.recordId });
    setDeleting(false);

    if (error) return alert(error.message);
    deleteNode?.();
  };

  const videoId = attrs.youtubeVideoId ?? parsed.videoId ?? null;
  const startSeconds = attrs.startSeconds ?? parsed.startSeconds ?? 0;
  const origin = useMemo(() => {
    try {
      return window.location.origin;
    } catch {
      return null;
    }
  }, []);
  const embedUrl = useMemo(
    () => buildEmbedUrl(videoId, startSeconds, origin),
    [videoId, startSeconds, origin]
  );

  useEffect(() => {
    const el = playerRef.current;
    if (!el || !embedUrl) return;
    if (typeof ResizeObserver === "undefined") return;

    let lastSig = "";
    const ro = new ResizeObserver((entries) => {
      const cr = entries?.[0]?.contentRect;
      if (!cr) return;
      const sig = `${Math.round(cr.width)}x${Math.round(cr.height)}`;
      if (!lastSig) {
        lastSig = sig;
        return;
      }
      if (sig !== lastSig) {
        lastSig = sig;
        // Remount iframe so YouTube recalculates layout for the new box size.
        setIframeKey((k) => k + 1);
      }
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, [embedUrl]);

  return (
    <>
      <NodeViewWrapper
        as="div"
        contentEditable={false}
        role="group"
        tabIndex={-1}
        className="relative group w-full rounded-2xl bg-slate-50 shadow-sm p-4 border border-slate-200"
        onMouseDownCapture={(e) => {
          if (!isFormControl(e.target)) e.preventDefault();
          e.stopPropagation();
        }}
        onTouchStartCapture={(e) => {
          if (!isFormControl(e.target)) e.preventDefault();
          e.stopPropagation();
        }}
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 mb-4 min-w-0">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-red-100 text-red-600 rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiVideo} className="w-6 h-6" />
            </div>
            <div>
              <div className="text-lg font-bold text-slate-800">YouTube Video</div>
              <div className="text-sm text-slate-500">Paste a link and it will embed in the note.</div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 flex-wrap sm:flex-nowrap min-w-0">
            {/* Save / Update */}
            {canEdit && (
              <button
                className={cls(
                  "px-2.5 py-1.5 sm:px-3 rounded-lg text-sm font-semibold border transition-all flex items-center gap-2 whitespace-nowrap max-w-full",
                  canSave
                    ? "bg-white text-slate-700 border-slate-300 hover:bg-slate-100 hover:border-slate-400"
                    : "bg-slate-200 text-slate-500 border-slate-200 cursor-not-allowed"
                )}
                disabled={!canSave || saving}
                onClick={handleSave}
                title={!effectiveLessonNoteId ? "Missing lesson note context" : (!parsed.ok ? "Paste a valid YouTube link" : "Save")}
              >
                <SafeIcon icon={FiSave} />
                {saving ? "Saving..." : saved ? "Update" : "Save"}
              </button>
            )}

            {/* Open on YouTube */}
            {(attrs.youtubeUrl || url) && (
              <a
                href={attrs.youtubeUrl || url}
                target="_blank"
                rel="noreferrer"
                className="p-2 rounded-full text-slate-500 hover:bg-slate-200 hover:text-slate-700 transition-colors"
                title="Open on YouTube"
              >
                <SafeIcon icon={FiExternalLink} className="w-5 h-5" />
              </a>
            )}

            {/* Delete */}
            {canEdit && (
              <button
                className="p-2 rounded-full text-slate-500 hover:bg-red-100 hover:text-red-600 transition-colors opacity-50 group-hover:opacity-100"
                disabled={deleting}
                onClick={handleDelete}
                title="Delete Tool"
              >
                <SafeIcon icon={FiTrash2} className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        {/* Body */}
        {canEdit && (
          <div className="bg-white/80 rounded-xl p-4 border border-dashed border-slate-200 mb-4">
            <label className="text-xs font-semibold text-slate-600 mb-1 block">YouTube link</label>
            <input
              type="text"
              className={cls(
                "w-full border-slate-300 rounded-lg shadow-sm transition",
                "focus:ring-red-500 focus:border-red-500"
              )}
              placeholder="https://www.youtube.com/watch?v=…"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
            {!parsed.ok && url.trim() !== "" && (
              <div className="text-xs text-red-600 mt-2">
                That doesn’t look like a valid YouTube link yet.
              </div>
            )}
            {parsed.ok && (
              <div className="text-xs text-slate-500 mt-2">
                Video ID: <span className="font-mono">{parsed.videoId}</span>
                {parsed.startSeconds ? <> • Start: {parsed.startSeconds}s</> : null}
              </div>
            )}
          </div>
        )}

        {/* Player */}
        {embedUrl ? (
          <div className="rounded-xl overflow-hidden border border-slate-200 bg-black">
            <div
              ref={playerRef}
              className="relative w-full"
              style={{ paddingTop: "56.25%" }}
            >
              <iframe
                title="YouTube player"
                src={embedUrl}
                className="absolute inset-0 w-full h-full yt-embed"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </div>
          </div>
        ) : (
          !canEdit && (
            <div className="text-sm text-slate-600 bg-white/80 rounded-xl p-4 border border-dashed border-slate-200">
              No video saved yet.
            </div>
          )
        )}
      </NodeViewWrapper>
    </>
  );
}

const YouTubeLinkNode = Node.create({
  name: "youtubeLink",
  group: "block",
  atom: true,
  selectable: true,
  addAttributes() {
    return {
      noteBlockId: { default: null },
      lessonNoteId: { default: null },
      schoolId: { default: null },
      teacherId: { default: null },
      studentId: { default: null },
      studentMasterId: { default: null },

      recordId: { default: null },
      youtubeUrl: { default: null },
      youtubeVideoId: { default: null },
      startSeconds: { default: 0 },
    };
  },
  parseHTML() {
    return [{ tag: "youtube-link" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["youtube-link", mergeAttributes(HTMLAttributes)];
  },
  addNodeView() {
    return ReactNodeViewRenderer(YouTubeLinkBlock);
  },
});

export default YouTubeLinkNode;